import { useState, useEffect } from 'react'
import { fmtDateTime } from '../../utils/format'
import DateInput from '../../components/DateInput'
import {
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'

interface Zone {
  id: number
  name: string
  code: string
}

interface Refrigerator {
  id: number
  name: string
  code: string
  type: string
  location: string
  min_temperature: number
  max_temperature: number
  target_temperature?: number
  zone_id: number
  zone_name?: string
}

interface TemperatureRecord {
  id: number
  ccp: {
    id: number
    code: string
    name: string
  }
  controllable_type: string
  controllable_id: number
  controllable?: {
    id: number
    name: string
    code: string
    location: string
  }
  location: string
  measured_temperature: number
  target_temperature?: number
  min_temperature: number
  max_temperature: number
  is_compliant: boolean
  deviation_reason?: string
  corrective_action?: string
  measured_at: string
  measured_by?: {
    id: number
    name: string
  }
  notes?: string
  created_at: string
}

interface Ccp {
  id: number
  code: string
  name: string
  type: string
  critical_limit_min: string | number | null
  critical_limit_max: string | number | null
  is_active: boolean
}

const API_BASE_URL = '/api/v1/dev'

export default function TemperatureRecords() {
  const [records, setRecords] = useState<TemperatureRecord[]>([])
  const [refrigerators, setRefrigerators] = useState<Refrigerator[]>([])
  const [zones, setZones] = useState<Zone[]>([])
  const [ccps, setCcps] = useState<Ccp[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<TemperatureRecord | null>(null)
  const [editingRecord, setEditingRecord] = useState<TemperatureRecord | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dateFilter, setDateFilter] = useState<string>('')
  const [equipmentFilter, setEquipmentFilter] = useState<string>('all')
  const [complianceFilter, setComplianceFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const perPage = 15

  // Form data
  const [formData, setFormData] = useState({
    ccp_id: '',
    controllable_id: '',
    zone_id: '',
    measured_temperature: '',
    target_temperature: '',
    min_temperature: '',
    max_temperature: '',
    deviation_reason: '',
    corrective_action: '',
    measured_at: '',
    notes: ''
  })

  // Fetch temperature records
  const fetchRecords = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams()
      // Traemos todos los registros: el filtrado y la paginación se hacen en cliente
      params.append('per_page', '100000')
      if (dateFilter) {
        params.append('date_from', dateFilter)
        params.append('date_to', dateFilter)
      }

      const response = await fetch(`${API_BASE_URL}/temperature-controls?${params}`)
      const data = await response.json()

      if (response.ok) {
        setRecords(data.data?.data || [])
      } else {
        setError('Error al cargar registros de temperatura')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error fetching records:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch refrigerators (only refrigeration equipment, not machinery)
  const fetchRefrigerators = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/refrigerators`)
      const data = await response.json()
      if (response.ok) {
        // Filter out machinery equipment since it doesn't need temperature control
        const refrigerationEquipment = (data.data || []).filter((equipment: Refrigerator) =>
          equipment.type !== 'maquinaria'
        )
        setRefrigerators(refrigerationEquipment)
      }
    } catch (err) {
      console.error('Error fetching refrigerators:', err)
    }
  }

  // Fetch zones
  const fetchZones = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/zones`)
      const data = await response.json()
      if (response.ok) {
        setZones(data.data || [])
      }
    } catch (err) {
      console.error('Error fetching zones:', err)
    }
  }

  // Carga los Puntos de Control Crítico de tipo temperatura (para el selector)
  const fetchCcps = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/haccp/ccps`)
      const data = await response.json()
      if (response.ok && data.success) {
        setCcps((data.data || []).filter((c: Ccp) => c.type === 'temperature' && c.is_active))
      }
    } catch (err) {
      console.error('Error fetching ccps:', err)
    }
  }

  useEffect(() => {
    fetchRecords()
    fetchRefrigerators()
    fetchZones()
    fetchCcps()
  }, [dateFilter])

  // Filter records
  const filteredRecords = records.filter(record => {
    const equipmentMatch = equipmentFilter === 'all' || record.controllable_id.toString() === equipmentFilter
    const complianceMatch = complianceFilter === 'all' ||
      (complianceFilter === 'compliant' ? record.is_compliant : !record.is_compliant)
    return equipmentMatch && complianceMatch
  })

  const totalPages = Math.ceil(filteredRecords.length / perPage)
  const paginatedRecords = filteredRecords.slice((currentPage - 1) * perPage, currentPage * perPage)

  useEffect(() => { setCurrentPage(1) }, [equipmentFilter, complianceFilter, dateFilter])

  // Get compliance badge
  const getComplianceBadge = (isCompliant: boolean) => {
    if (isCompliant) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Conforme
        </span>
      )
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="w-3 h-3 mr-1" />
          No Conforme
        </span>
      )
    }
  }

  // Handle create or update
  const handleSave = async () => {
    if (!formData.controllable_id || !formData.ccp_id) {
      setError('Selecciona un equipo')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const url = editingRecord
        ? `${API_BASE_URL}/temperature-controls/${editingRecord.id}`
        : `${API_BASE_URL}/temperature-controls`

      const method = editingRecord ? 'PUT' : 'POST'

      // Get zone name from zone_id
      const selectedZone = zones.find(z => z.id.toString() === formData.zone_id)

      const payload = {
        ccp_id: parseInt(formData.ccp_id),
        controllable_type: 'App\\Domains\\HACCP\\Models\\Refrigerator',
        controllable_id: parseInt(formData.controllable_id),
        location: selectedZone?.name || '', // Use zone name as location
        measured_temperature: parseFloat(formData.measured_temperature),
        target_temperature: formData.target_temperature ? parseFloat(formData.target_temperature) : null,
        min_temperature: parseFloat(formData.min_temperature),
        max_temperature: parseFloat(formData.max_temperature),
        deviation_reason: formData.deviation_reason || null,
        corrective_action: formData.corrective_action || null,
        measured_at: formData.measured_at,
        notes: formData.notes || null
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (response.ok) {
        await fetchRecords()
        setShowCreateModal(false)
        setEditingRecord(null)
        resetForm()
      } else {
        if (data.errors) {
          const messages = Object.values(data.errors).flat().join('\n')
          setError(messages)
        } else {
          setError(data.message || 'Error al guardar el registro')
        }
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error saving record:', err)
    } finally {
      setSaving(false)
    }
  }

  // Delete record
  const deleteRecord = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar este registro?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/temperature-controls/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        await fetchRecords()
      } else {
        const data = await response.json()
        setError(data.message || 'Error al eliminar el registro')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error deleting record:', err)
    }
  }

  // View record details
  const viewRecord = (record: TemperatureRecord) => {
    setSelectedRecord(record)
    setShowViewModal(true)
  }

  // Open create modal
  const openCreateModal = () => {
    setEditingRecord(null)
    resetForm()
    // Set current date and time
    const now = new Date()
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
    setFormData(prev => ({ ...prev, measured_at: localDateTime }))
    setShowCreateModal(true)
    setError(null)
  }

  // Edit record
  const editRecord = (record: TemperatureRecord) => {
    // Find the zone id based on the location name (assuming location contains zone name)
    const zone = zones.find(z => z.name === record.location)

    setEditingRecord(record)
    setFormData({
      ccp_id: record.ccp.id.toString(),
      controllable_id: record.controllable_id.toString(),
      zone_id: zone?.id.toString() || '',
      measured_temperature: record.measured_temperature.toString(),
      target_temperature: record.target_temperature?.toString() || '',
      min_temperature: record.min_temperature.toString(),
      max_temperature: record.max_temperature.toString(),
      deviation_reason: record.deviation_reason || '',
      corrective_action: record.corrective_action || '',
      measured_at: new Date(record.measured_at).toISOString().slice(0, 16),
      notes: record.notes || ''
    })
    setShowCreateModal(true)
    setError(null)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      ccp_id: '',
      controllable_id: '',
      zone_id: '',
      measured_temperature: '',
      target_temperature: '',
      min_temperature: '',
      max_temperature: '',
      deviation_reason: '',
      corrective_action: '',
      measured_at: '',
      notes: ''
    })
  }

  // Handle refrigerator selection
  // Desplaza el filtro de fecha un día (parte de hoy si está vacío)
  const shiftDateFilter = (delta: number) => {
    const base = dateFilter || new Date().toLocaleDateString('sv-SE') // yyyy-mm-dd local
    const [y, m, d] = base.split('-').map(Number)
    const dt = new Date(y, m - 1, d)
    dt.setDate(dt.getDate() + delta)
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
    setDateFilter(iso)
  }

  const handleRefrigeratorChange = (refrigeratorId: string) => {
    const refrigerator = refrigerators.find(r => r.id.toString() === refrigeratorId)
    if (refrigerator) {
      // El PCC se asigna automáticamente según el tipo de equipo:
      // frío positivo -> CCP-002 (almacenamiento en frío); frío negativo -> CCP-003 (congelado)
      const ccpCode = refrigerator.type === 'negative_cold' ? 'CCP-003' : 'CCP-002'
      const ccp = ccps.find(c => c.code === ccpCode)
      setFormData({
        ...formData,
        controllable_id: refrigeratorId,
        ccp_id: ccp ? ccp.id.toString() : '',
        zone_id: refrigerator.zone_id.toString(),
        min_temperature: refrigerator.min_temperature.toString(),
        max_temperature: refrigerator.max_temperature.toString(),
        target_temperature: refrigerator.target_temperature?.toString() || ''
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Registros de Temperatura</h1>
          <p className="mt-1 text-sm text-gray-500">
            Registra y monitorea las temperaturas de los equipos
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Nuevo Registro
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Fecha</label>
            <div className="mt-1 flex items-center gap-1">
              <button
                type="button"
                onClick={() => shiftDateFilter(-1)}
                title="Día anterior"
                className="p-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <DateInput
                value={dateFilter}
                onChange={(v) => setDateFilter(v)}
                className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
              />
              <button
                type="button"
                onClick={() => shiftDateFilter(1)}
                title="Día siguiente"
                className="p-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Equipo</label>
            <select
              value={equipmentFilter}
              onChange={(e) => setEquipmentFilter(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="all">Todos</option>
              {refrigerators.map((refrigerator) => (
                <option key={refrigerator.id} value={refrigerator.id}>
                  {refrigerator.name} - {refrigerator.location}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Conformidad</label>
            <select
              value={complianceFilter}
              onChange={(e) => setComplianceFilter(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="all">Todos</option>
              <option value="compliant">Conforme</option>
              <option value="non_compliant">No Conforme</option>
            </select>
          </div>
        </div>
      </div>

      {/* Records Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredRecords.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <BeakerIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay registros</h3>
          <p className="mt-1 text-sm text-gray-500">Comienza registrando una nueva temperatura.</p>
          <div className="mt-6">
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Nuevo Registro
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Equipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zona
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Temperatura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rango Permitido
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conformidad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha/Hora
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedRecords.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {record.controllable?.name || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {record.controllable?.code || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{record.location}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${
                      record.is_compliant ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {parseFloat(String(record.measured_temperature)).toFixed(1)}°C
                    </div>
                    {record.target_temperature && (
                      <div className="text-xs text-gray-500">
                        Objetivo: {parseFloat(String(record.target_temperature)).toFixed(1)}°C
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {parseFloat(String(record.min_temperature)).toFixed(1)}°C a {parseFloat(String(record.max_temperature)).toFixed(1)}°C
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getComplianceBadge(record.is_compliant)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {fmtDateTime(record.measured_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => viewRecord(record)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Ver detalles"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => editRecord(record)}
                        className="text-green-600 hover:text-green-900"
                        title="Editar"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => deleteRecord(record.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <span className="text-sm text-gray-700">
                Página {currentPage} de {totalPages} ({filteredRecords.length} registros)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-2xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingRecord ? 'Editar Registro' : 'Nuevo Registro de Temperatura'}
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Equipo *
                      </label>
                      <select
                        value={formData.controllable_id}
                        onChange={(e) => handleRefrigeratorChange(e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      >
                        <option value="">Seleccionar equipo</option>
                        {refrigerators.map((refrigerator) => (
                          <option key={refrigerator.id} value={refrigerator.id}>
                            {refrigerator.name} - {refrigerator.location}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fecha y Hora *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.measured_at}
                        onChange={(e) => setFormData({ ...formData, measured_at: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Zona
                    </label>
                    <input
                      type="text"
                      value={zones.find(z => z.id.toString() === formData.zone_id)?.name || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-100"
                      readOnly
                      placeholder="Se carga automáticamente al seleccionar el equipo"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Temperatura Medida (°C) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.measured_temperature}
                        onChange={(e) => setFormData({ ...formData, measured_temperature: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Temp. Mínima (°C) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.min_temperature}
                        onChange={(e) => setFormData({ ...formData, min_temperature: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-100"
                        required
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Temp. Máxima (°C) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.max_temperature}
                        onChange={(e) => setFormData({ ...formData, max_temperature: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-100"
                        required
                        readOnly
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Temperatura Objetivo (°C)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.target_temperature}
                      onChange={(e) => setFormData({ ...formData, target_temperature: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-100"
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Razón de Desviación
                    </label>
                    <input
                      type="text"
                      value={formData.deviation_reason}
                      onChange={(e) => setFormData({ ...formData, deviation_reason: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Solo si la temperatura está fuera de rango..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Acción Correctiva
                    </label>
                    <textarea
                      value={formData.corrective_action}
                      onChange={(e) => setFormData({ ...formData, corrective_action: e.target.value })}
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Acciones tomadas para corregir la desviación..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Notas
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Observaciones adicionales..."
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Guardando...' : (editingRecord ? 'Actualizar' : 'Registrar')}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedRecord && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-2xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Detalle del Registro de Temperatura
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Equipo</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedRecord.controllable?.name}</p>
                      <p className="text-xs text-gray-500">{selectedRecord.controllable?.code}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Conformidad</p>
                      <div className="mt-1">
                        {getComplianceBadge(selectedRecord.is_compliant)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Zona</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedRecord.location}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Fecha y Hora</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {fmtDateTime(selectedRecord.measured_at)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Temperatura Medida</p>
                      <p className={`mt-1 text-lg font-semibold ${
                        selectedRecord.is_compliant ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {parseFloat(String(selectedRecord.measured_temperature)).toFixed(1)}°C
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Rango Permitido</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {parseFloat(String(selectedRecord.min_temperature)).toFixed(1)}°C a {parseFloat(String(selectedRecord.max_temperature)).toFixed(1)}°C
                      </p>
                    </div>
                    {selectedRecord.target_temperature && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Temperatura Objetivo</p>
                        <p className="mt-1 text-sm text-gray-900">{selectedRecord.target_temperature}°C</p>
                      </div>
                    )}
                  </div>

                  {selectedRecord.deviation_reason && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Razón de Desviación</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedRecord.deviation_reason}</p>
                    </div>
                  )}

                  {selectedRecord.corrective_action && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Acción Correctiva</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedRecord.corrective_action}</p>
                    </div>
                  )}

                  {selectedRecord.measured_by && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Medido por</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedRecord.measured_by.name}</p>
                    </div>
                  )}

                  {selectedRecord.notes && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Notas</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedRecord.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}